import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { isNative } from './platform.js';
import { wrapGlobal } from '../runtime/legacy.js';

/**
 * Haptic feedback.
 *
 * On native this is the Taptic Engine / Android vibrator; on the web it falls
 * back to `navigator.vibrate` where available and is a no-op otherwise. Every
 * call is fire-and-forget — a device with haptics disabled must never surface
 * an error to the user.
 */

function webVibrate(pattern) {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported */
  }
}

async function safely(nativeCall, webPattern) {
  try {
    if (isNative) await nativeCall();
    else webVibrate(webPattern);
  } catch {
    /* haptics are decoration; never let them break a flow */
  }
}

/** Light tap — taps, toggles, tab switches. */
export const tap = () => safely(() => Haptics.impact({ style: ImpactStyle.Light }), 10);

/** Medium tap — completing a set, claiming a mission. */
export const impact = () => safely(() => Haptics.impact({ style: ImpactStyle.Medium }), 20);

/** Success pattern — workout finished, purchase complete. */
export const success = () =>
  safely(() => Haptics.notification({ type: NotificationType.Success }), [15, 60, 15]);

/** Warning/error pattern — failed validation, rejected purchase. */
export const error = () =>
  safely(() => Haptics.notification({ type: NotificationType.Error }), [40, 60, 40]);

/**
 * Wires haptics to the interactions where they carry meaning, using delegation
 * so newly rendered views are covered without re-binding.
 *
 * Deliberately restrained: a buzz on every tap reads as noise, and App Store
 * reviewers notice. Only actions that change state get feedback.
 */
export function installHaptics() {
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (target.closest('.bottom-nav-item, .nav-item')) return tap();
      if (target.closest('[class*="set-complete"], .btn-set-done, input[type="checkbox"]')) {
        return impact();
      }
      if (target.closest('.btn-primary, .btn-danger')) return tap();
    },
    { passive: true },
  );

  // Outcome feedback rides on the toast system, which every success and failure
  // path in the core already goes through.
  wrapGlobal('showToast', function (next, message, type = 'success', duration) {
    if (type === 'error') error();
    else if (type === 'success') success();
    return next(message, type, duration);
  });
}
